import classNames from 'classnames';
import * as React from 'react';
import type { GobiStoryProps } from '../GobiStory/GobiStory';
import { GobiStory } from '../GobiStory/GobiStory';
import { PageSection } from '~/Features/Layouts/Components/PageSection/PageSection';
import { PageSectionHeading } from '~/Features/Layouts/Components/PageSectionHeading/PageSectionHeading';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { OverlayContainerProps } from '~/Features/StudyPage/Campus/Components/Overlay/WithOverlay';
import { WithOverlay } from '~/Features/StudyPage/Campus/Components/Overlay/WithOverlay';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { Button } from '~/Partials/Button/Button';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import type { BaseImage } from '~/Partials/Picture/Picture';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { PicturePlaceholder } from '~/Partials/Picture/Placeholder/PicturePlaceholder';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import type { Theme } from '~/Utils/Theme';
import styles from './GobiSection.module.css';

export interface GobiSectionProps {
    title?: HtmlString;
    id?: string;
    content?: HtmlString;
    gobi?: GobiStoryProps;
    imageFallback?: BaseImage;
    overlay?: OverlayContainerProps<HtmlString>;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

const GobiSectionOverlayContent: React.FC<
    Pick<OverlayContainerProps<HtmlString>, 'overlayLabel' | 'content'>
> = props => {
    const { overlayLabel, content } = props;

    return (
        <>
            <h3 className={styles.OverlayHeading}>{overlayLabel}</h3>
            <HtmlDiv className="RichtextContent RichtextContent--dark" html={content} />
        </>
    );
};

export const GobiSection: React.FC<GobiSectionProps> = props => {
    const contextualTheme = useTheme();
    const { id, title, content, gobi, imageFallback, overlay, theme = contextualTheme } = props;

    const renderMedia = () => {
        if (gobi) {
            return <GobiStory {...gobi} theme={theme} className={styles.Thumbnail} />;
        }

        if (imageFallback) {
            return (
                <Picture
                    {...imageFallback}
                    profile={PictureProfiles.vertical}
                    theme={theme}
                    className={styles.Thumbnail}
                />
            );
        }

        return <PicturePlaceholder theme={theme} className={styles.Thumbnail} />;
    };

    return (
        <PageSection id={id} className={styles.GobiSection}>
            {title && <PageSectionHeading heading={title} className={styles.Title} />}
            <div className={styles.Content}>
                <div className={styles.TextSubsection}>
                    {content && (
                        <HtmlDiv
                            className={classNames(styles.IntroText, 'RichtextContent', `RichtextContent--light`)}
                            html={content}
                        />
                    )}
                    {overlay && (
                        <WithOverlay
                            overlayLabel={overlay.overlayLabel}
                            overlayContent={
                                <GobiSectionOverlayContent
                                    content={overlay.content}
                                    overlayLabel={overlay.overlayLabel}
                                />
                            }
                            closeLabel={overlay.closeLabel}
                        >
                            {triggerButtonProps => (
                                <Button
                                    type={ButtonType.Clear}
                                    className={styles.OverlayButton}
                                    title={overlay.openLabel}
                                    {...triggerButtonProps}
                                >
                                    <span className="visuallyHidden">{overlay.openLabel}</span>
                                    <SvgIcon iconName="plus-circle" className={styles.OverlayIcon} />
                                </Button>
                            )}
                        </WithOverlay>
                    )}
                </div>
                <div className={styles.Separator} />
                <div className={styles.VideoSubsection}>{renderMedia()}</div>
            </div>
        </PageSection>
    );
};
